/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Warning,
  ArrowLeft,
  Calendar,
  Chat,
  Check,
  Clock,
  Footprints,
  Gear,
  Info,
  Megaphone,
  Palette,
  PaperPlaneTilt,
  Shield,
  SignOut,
  Smiley,
  Star,
  Trash,
  UserPlus,
  Users,
  X
} from '@phosphor-icons/react';
import { API_BASE as API } from '../apiConfig';


const TYPE_ICONS = {
  walk: Footprints, discussion: Chat, event: Calendar,
  interest: Star, creative: Palette, action: Megaphone,
};

const REACTIONS = ['👍', '❤️', '😂', '🤔', '👏', '🔥'];

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function GroupChat({ group, token, user, onBack, showMessage }) {
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showMembers, setShowMembers] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [reactionPicker, setReactionPicker] = useState(null); // messageId
  const [pendingMsg, setPendingMsg] = useState(null); // show moderation pending notice
  const messagesEndRef = useRef(null);
  const chatRef = useRef(null);
  const pollRef = useRef(null);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  const TypeIcon = TYPE_ICONS[group.type] || Star;
  const isOwner = group.createdBy === user?._id || group.createdBy?._id === user?._id;

  // ── Fetch messages ─────────────────────────────────
  const fetchMessages = useCallback(async (scroll = false) => {
    try {
      const res = await fetch(`${API}/api/connect/groups/${group._id}/messages?limit=100`, { headers });
      const data = await res.json();
      if (data.success) {
        setMessages(data.data || []);
        if (scroll) setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    } catch (err) { console.error('Messages fetch error:', err); }
    setLoading(false);
  }, [group._id, token]);

  useEffect(() => {
    fetchMessages(true);
    // Poll every 5 seconds for new messages
    pollRef.current = setInterval(() => fetchMessages(false), 5000);
    return () => clearInterval(pollRef.current);
  }, [fetchMessages]);

  // ── Send message ───────────────────────────────────
  const handleSend = async () => {
    const text = newMsg.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/api/connect/groups/${group._id}/messages`, {
        method: 'POST', headers, body: JSON.stringify({ content: text }),
      });
      const data = await res.json();
      if (data.success) {
        setNewMsg('');
        if (data.data?.moderationStatus === 'pending') {
          setPendingMsg('Your message is being reviewed by the moderator...');
          setTimeout(() => setPendingMsg(null), 3000);
        }
        fetchMessages(true);
      } else {
        if (data.data?.moderationStatus === 'rejected') {
          showMessage?.('Message was flagged by our moderator. Please keep the discussion respectful.');
        } else {
          showMessage?.(data.message || 'Could not send message');
        }
      }
    } catch (err) { showMessage?.('Error sending message'); }
    setSending(false);
  };

  // ── React to message ──────────────────────────────
  const handleReact = async (messageId, emoji) => {
    setReactionPicker(null);
    try {
      await fetch(`${API}/api/connect/groups/${group._id}/messages/${messageId}/react`, {
        method: 'POST', headers, body: JSON.stringify({ emoji }),
      });
      fetchMessages();
    } catch (err) { console.error('React error:', err); }
  };

  // ── Leave group ────────────────────────────────────
  const handleLeave = async () => {
    if (!window.confirm('Leave this group?')) return;
    try {
      const res = await fetch(`${API}/api/connect/groups/${group._id}/leave`, { method: 'POST', headers });
      const data = await res.json();
      if (data.success) { showMessage?.('Left group'); onBack?.(); }
      else showMessage?.(data.message || 'Error leaving group');
    } catch (err) { showMessage?.('Error leaving group'); }
  };

  // ── Close group (owner only) ───────────────────────
  const handleClose = async () => {
    if (!window.confirm('Close this group? It will be archived.')) return;
    try {
      const res = await fetch(`${API}/api/connect/groups/${group._id}/close`, {
        method: 'POST', headers, body: JSON.stringify({ reason: 'Owner closed group' }),
      });
      const data = await res.json();
      if (data.success) { showMessage?.('Group closed'); onBack?.(); }
      else showMessage?.(data.message || 'Error closing group');
    } catch (err) { showMessage?.('Error closing group'); }
  };

  // ── Render ─────────────────────────────────────────
  const memberCount = group.memberCount || group.members?.length || 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100dvh - 60px)' }}>
      {/* ── Header ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
        background: '#18181b', borderBottom: '1px solid #27272a', flexShrink: 0,
      }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#a1a1aa', cursor: 'pointer', padding: 4 }}
          aria-label="Back to groups">
          <ArrowLeft size={20} />
        </button>
        <TypeIcon size={18} color="#2dd4bf" aria-hidden="true" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ color: '#fafafa', fontSize: 14, fontWeight: 700, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {group.name}
          </h3>
          <span style={{ color: '#71717a', fontSize: 11 }}>{memberCount} members</span>
        </div>
        <button onClick={() => setShowInfo(!showInfo)}
          style={{ background: 'none', border: 'none', color: '#71717a', cursor: 'pointer', padding: 4 }}
          aria-label="Group info">
          <Info size={18} />
        </button>
        <button onClick={() => setShowMembers(!showMembers)}
          style={{ background: 'none', border: 'none', color: '#71717a', cursor: 'pointer', padding: 4 }}
          aria-label="Show members">
          <Users size={18} />
        </button>
      </div>

      {/* ── Info panel ── */}
      {showInfo && (
        <div style={{
          padding: '12px 16px', background: '#0a0a0a', borderBottom: '1px solid #27272a',
          fontSize: 13, color: '#d4d4d8', lineHeight: 1.5, flexShrink: 0,
        }}>
          <p style={{ margin: '0 0 8px' }}>{group.description || 'No description'}</p>
          {group.interests?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
              {group.interests.map(t => (
                <span key={t} style={{ background: 'rgba(45,212,191,0.1)', color: '#2dd4bf', fontSize: 10, padding: '3px 8px', borderRadius: 10, fontWeight: 600 }}>{t}</span>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button onClick={handleLeave} style={{
              display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px',
              background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)',
              borderRadius: 8, color: '#f87171', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}><SignOut size={12} /> Leave</button>
            {isOwner && (
              <button onClick={handleClose} style={{
                display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px',
                background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.3)',
                borderRadius: 8, color: '#fbbf24', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              }}><Trash size={12} /> Close Group</button>
            )}
          </div>
        </div>
      )}

      {/* ── Members panel ── */}
      {showMembers && group.members && (
        <div style={{
          padding: '10px 16px', background: '#0a0a0a', borderBottom: '1px solid #27272a',
          maxHeight: 200, overflowY: 'auto', flexShrink: 0,
        }}>
          <h4 style={{ color: '#a1a1aa', fontSize: 11, fontWeight: 700, margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: 1 }}>
            Members ({memberCount})
          </h4>
          {group.members.map((m, i) => {
            const memberId = m.userId?._id || m.userId;
            const name = m.userId?.firstName || m.userId?.name || 'Member';
            const isOwnr = memberId === (group.createdBy?._id || group.createdBy);
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                <div style={{
                  width: 24, height: 24, borderRadius: '50%',
                  background: isOwnr ? 'rgba(45,212,191,0.2)' : 'rgba(255,255,255,0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, color: isOwnr ? '#2dd4bf' : '#a1a1aa', fontWeight: 700,
                }}>{name[0]?.toUpperCase()}</div>
                <span style={{ color: '#d4d4d8', fontSize: 12, flex: 1 }}>{name}</span>
                {isOwnr && <span style={{ color: '#2dd4bf', fontSize: 9, fontWeight: 700 }}>OWNER</span>}
                {m.role === 'moderator' && <Shield size={10} color="#fbbf24" />}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Messages ── */}
      <div ref={chatRef} style={{
        flex: 1, overflowY: 'auto', padding: '12px 16px',
        display: 'flex', flexDirection: 'column', gap: 4,
      }} role="log" aria-label="Group messages" aria-live="polite">
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#71717a', fontSize: 13 }}>Loading messages...</div>
        ) : messages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Chat size={28} color="#3f3f46" aria-hidden="true" />
            <p style={{ color: '#71717a', fontSize: 13, marginTop: 8 }}>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((msg, i) => {
            const isMe = (msg.userId?._id || msg.userId) === user?._id;
            const name = msg.userId?.firstName || msg.userId?.name || 'Member';
            const isFlagged = msg.moderationStatus === 'flagged';
            const isPending = msg.moderationStatus === 'pending';

            return (
              <div key={msg._id || i}
                style={{
                  display: 'flex', flexDirection: 'column',
                  alignItems: isMe ? 'flex-end' : 'flex-start',
                  marginBottom: 2,
                }}
                role="article"
                aria-label={`${name} said: ${msg.content}`}
              >
                {/* Name + time */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                  {!isMe && <span style={{ color: '#2dd4bf', fontSize: 11, fontWeight: 600 }}>{name}</span>}
                  <span style={{ color: '#52525b', fontSize: 9 }}>{timeAgo(msg.createdAt)}</span>
                  {isFlagged && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, color: '#fbbf24', fontSize: 9 }}>
                      <Warning size={9} /> Reviewed
                    </span>
                  )}
                </div>

                {/* Bubble */}
                <div
                  onClick={() => setReactionPicker(reactionPicker === msg._id ? null : msg._id)}
                  style={{
                    maxWidth: '80%', padding: '8px 12px', borderRadius: 12,
                    background: isMe ? 'rgba(45,212,191,0.15)' : '#18181b',
                    border: isFlagged ? '1px solid rgba(251,191,36,0.3)' : '1px solid #27272a',
                    color: '#e4e4e7', fontSize: 13, lineHeight: 1.4, cursor: 'pointer',
                    opacity: isPending ? 0.5 : 1,
                  }}>
                  {isPending ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#71717a', fontStyle: 'italic' }}>
                      <Clock size={11} /> Being reviewed...
                    </span>
                  ) : (
                    msg.content
                  )}
                </div>

                {/* Reactions */}
                {msg.reactions && Object.keys(msg.reactions).length > 0 && (
                  <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
                    {Object.entries(msg.reactions).map(([emoji, reactors]) => (
                      <button key={emoji} onClick={() => handleReact(msg._id, emoji)}
                        aria-label={`${emoji} reaction, ${Array.isArray(reactors) ? reactors.length : 0} people`}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 2,
                          padding: '2px 6px', borderRadius: 10, border: '1px solid #27272a',
                          background: '#0a0a0a', fontSize: 12, cursor: 'pointer', color: '#a1a1aa',
                        }}>
                        {emoji} <span style={{ fontSize: 9 }}>{Array.isArray(reactors) ? reactors.length : 0}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Reaction picker */}
                {reactionPicker === msg._id && (
                  <div style={{
                    display: 'flex', gap: 4, marginTop: 4, padding: '4px 8px',
                    background: '#18181b', border: '1px solid #27272a', borderRadius: 20,
                  }} role="toolbar" aria-label="Add reaction">
                    {REACTIONS.map(emoji => (
                      <button key={emoji} onClick={() => handleReact(msg._id, emoji)}
                        style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', padding: '2px 4px' }}
                        aria-label={`React with ${emoji}`}>
                        {emoji}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* ── Pending notice ── */}
      {pendingMsg && (
        <div style={{
          padding: '8px 16px', background: 'rgba(45,212,191,0.08)',
          borderTop: '1px solid rgba(45,212,191,0.2)', fontSize: 12, color: '#2dd4bf',
          display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
        }}>
          <Shield size={12} aria-hidden="true" /> {pendingMsg}
        </div>
      )}

      {/* ── Closed notice ── */}
      {(group.status === 'closed' || group.status === 'archived') ? (
        <div style={{
          padding: '14px 16px', background: '#18181b', borderTop: '1px solid #27272a',
          textAlign: 'center', color: '#71717a', fontSize: 13, flexShrink: 0,
        }}>
          This group is {group.status}. Messages are read-only.
        </div>
      ) : (
        /* ── Input ── */
        <div style={{
          display: 'flex', gap: 8, padding: '10px 16px',
          background: '#18181b', borderTop: '1px solid #27272a', flexShrink: 0,
        }}>
          <input
            value={newMsg}
            onChange={e => setNewMsg(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSend())}
            placeholder="Type a message..."
            aria-label="Type a message"
            style={{
              flex: 1, padding: '10px 14px', background: '#09090b', border: '1px solid #27272a',
              borderRadius: 12, color: '#fafafa', fontSize: 13, fontFamily: 'inherit', outline: 'none',
            }}
          />
          <button onClick={handleSend} disabled={sending || !newMsg.trim()}
            aria-label="Send message"
            style={{
              padding: '10px 14px', background: newMsg.trim() ? '#2dd4bf' : '#27272a',
              border: 'none', borderRadius: 12, cursor: newMsg.trim() ? 'pointer' : 'default',
              color: newMsg.trim() ? '#000' : '#52525b', transition: 'all 0.2s',
            }}>
            <PaperPlaneTilt size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
